import { answered, binaryLabels, isFinding } from './evaluationIntegrity';
// Helpers compartidos para formatear respuestas de evaluación por
// question_type. Lo usan tanto la UI (read-only) como los generadores
// de PDF (Técnico y Ejecutivo).

const FIELD_OF_TYPE = {
  score_scale: 'score',
  yes_no: 'answer_bool',
  numeric: 'answer_numeric',
  date: 'answer_date',
  text_short: 'answer_text',
  text_long: 'answer_text',
  file_upload: 'evidence_urls',
  single_choice: 'selected_options',
  multiple_choice: 'selected_options',
  dropdown: 'selected_options',
};

/**
 * Devuelve true si una respuesta cuenta como "contestada". Espejo del
 * helper isQuestionAnswered del checklist; centralizado aquí para que
 * los PDFs lo usen también.
 */
export function isAnswered(criterion, item) {
  return !!item && answered(criterion, item);
}

/** #Fase1-10 — Un hallazgo bloqueante en cero es crítico; la captura deriva is_finding localmente. */
export function isCriticalFinding(criterion, item, local = false) {
  if (!item || !isFinding(criterion, item, local)) return false;
  if (!criterion?.blocker && !criterion?.is_blocking) return false;
  const type = criterion.question_type || 'score_scale';
  if (binaryLabels[type]) {
    return item.answer_bool !== (type === 'yes_no' ? criterion.favorable_answer === 'si' : true);
  }
  return item.score === 0;
}

/**
 * Formatea la respuesta como texto plano (para PDFs) según el tipo.
 * Devuelve cadena vacía o '—' cuando no hay respuesta.
 */
export function formatAnswerForPDF(criterion, item) {
  if (criterion?.question_type === 'info') return 'Informativo';
  if (item?.not_applicable) return `No aplica${item.na_reason ? ': ' + item.na_reason : ''}`;
  if (!item || !answered(criterion, item)) return 'Sin respuesta';
  const qtype = criterion?.question_type || 'score_scale';

  if (qtype === 'score_scale') {
    const s = item.score ?? 0;
    const max = criterion?.max_score ?? 10;
    return `${s}/${max}`;
  }

  if (binaryLabels[qtype]) {
    if (item.answer_bool === true) return binaryLabels[qtype][0];
    if (item.answer_bool === false) return binaryLabels[qtype][1];
    return '—';
  }

  if (qtype === 'dropdown' || qtype === 'single_choice') {
    const selected = (item.selected_options || [])[0];
    if (!selected) return '—';
    const opt = (criterion?.options || []).find(
      (o) => o.id === selected || o.label === selected || o.value === selected
    );
    return opt?.label || String(selected);
  }

  if (qtype === 'multiple_choice') {
    const selected = item.selected_options || [];
    if (!selected.length) return '—';
    return selected.map(v => {
        const opt = (criterion?.options || []).find(
          (o) => o.id === v || o.label === v || o.value === v
        );
      return opt?.label || String(v);
    }).join(', ');
  }

  if (qtype === 'numeric') {
    return item.answer_numeric !== null && item.answer_numeric !== undefined
      ? String(item.answer_numeric)
      : '—';
  }

  if (qtype === 'date') {
    if (!item.answer_date) return '—';
    try {
      // Aceptamos YYYY-MM-DD o ISO completo.
      const d = new Date(item.answer_date.length === 10 ? item.answer_date + 'T12:00:00' : item.answer_date);
      return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return String(item.answer_date); }
  }

  if (qtype === 'text_short' || qtype === 'text_long') {
    return item.answer_text ? String(item.answer_text) : '—';
  }

  if (qtype === 'file_upload') {
    const n = (item.evidence_urls || []).length;
    return n === 0 ? '—' : `${n} archivo${n !== 1 ? 's' : ''} adjunto${n !== 1 ? 's' : ''}`;
  }

  return '—';
}

export const ANSWER_FIELD_OF_TYPE = FIELD_OF_TYPE;
